import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router";

import { getApi, postApi, putApi, deleteApi } from "../../services/api/globalApi";
import {
  errorMessage,
  successMessage,
} from "../../utils/toastify-message/ToastifyMessage";

import Title from "../../components/common/title/Title";
import Table from "../../components/common/table/Table";
import Button from "../../components/common/button/Button";
import Input from "../../components/common/inputs/Input";
import Select from "../../components/common/inputs/Select";

const subAffiliateColumns = [
  { title: "نام", value: "name" },
  { title: "موبایل", value: "mobile" },
  { title: "شهر", value: "city" },
  { title: "درصد کمیسیون", value: "extreaRefDiscount" },
];

const manualAddValidation = (isEdit = false) =>
  Yup.object().shape({
    mobile: Yup.string()
      .required("وارد کردن موبایل الزامی است")
      .matches(/^09\d{9}$/, "شماره تماس اشتباه است . دقت کنید باید با 09 شروع شود"),
    password: isEdit
      ? Yup.string()
      : Yup.string()
          .required("وارد کردن رمز عبور الزامی است")
          .min(5, "رمز عبور خیلی کوتاه است!")
          .max(48, "رمز عبور خیلی بلند است!"),
    name: Yup.string().nullable(),
    address: Yup.string().nullable(),
    city: Yup.string().nullable(),
    commissionPercent: Yup.number()
      .nullable()
      .min(0, "درصد کمیسیون نمی‌تواند منفی باشد")
      .max(100, "درصد کمیسیون نمی‌تواند بیشتر از 100 باشد"),
  });

function findHeaderIndex(headerRow, candidates) {
  return headerRow?.findIndex((h) =>
    candidates.includes(String(h || "").trim())
  );
}

export default function AffiliateDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialAffiliate = location?.state;

  const [affiliate, setAffiliate] = useState(initialAffiliate);
  const [subAffiliates, setSubAffiliates] = useState([]);
  const [showManualForm, setShowManualForm] = useState(false);
  const [editingSubAffiliate, setEditingSubAffiliate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingParentPercent, setEditingParentPercent] = useState(false);
  const [parentPercentValue, setParentPercentValue] = useState(
    initialAffiliate?.extreaRefDiscount ?? ""
  );
  const [savingParentPercent, setSavingParentPercent] = useState(false);
  const fileInputRef = useRef(null);

  const getSubAffiliates = async () => {
    if (!affiliate?.id) return;
    try {
      const response = await getApi({
        path: `admin/users/${affiliate.id}/sub-affiliates`,
      });
      setSubAffiliates(response?.data?.result || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!affiliate?.id) {
      navigate("/user-list");
      return;
    }
    getSubAffiliates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitSubAffiliates = async (rows) => {
    try {
      const response = await postApi({
        path: `admin/users/${affiliate.id}/sub-affiliates`,
        body: { subAffiliates: rows },
      });
      const result = response?.data?.result || {};
      const createdCount = result.created?.length || 0;
      const failedCount = result.failed?.length || 0;
      if (createdCount) {
        successMessage(`${createdCount} زیرنماینده با موفقیت اضافه شد`);
      }
      if (failedCount) {
        const reasons = result.failed
          .map((f) => `${f.row?.mobile}: ${f.reason}`)
          .join(" | ");
        errorMessage(`${failedCount} ردیف ثبت نشد - ${reasons}`);
      }
      await getSubAffiliates();
    } catch (error) {
      errorMessage(
        error?.response?.data?.errors?.[0]?.message || "خطا در ثبت زیرنماینده‌ها"
      );
    }
  };

  const manualFormHandler = async (values, { resetForm }) => {
    const commissionPercent = values.commissionPercent
      ? Number(values.commissionPercent)
      : undefined;
    const sabooraAllowed = values.sabooraAllowed === "true";
    const raychatAllowed = values.raychatAllowed === "true";

    if (editingSubAffiliate) {
      try {
        await putApi({
          path: `admin/users/${affiliate.id}/sub-affiliates/${editingSubAffiliate._id}`,
          body: {
            name: values.name,
            address: values.address,
            city: values.city,
            commissionPercent,
            sabooraAllowed,
            raychatAllowed,
          },
        });
        successMessage("زیرنماینده با موفقیت ویرایش شد");
        await getSubAffiliates();
      } catch (error) {
        errorMessage(
          error?.response?.data?.errors?.[0]?.message || "خطا در ویرایش زیرنماینده"
        );
      }
    } else {
      await submitSubAffiliates([
        {
          mobile: values.mobile,
          password: values.password,
          name: values.name,
          address: values.address,
          city: values.city,
          commissionPercent,
          sabooraAllowed,
          raychatAllowed,
        },
      ]);
    }
    resetForm();
    setEditingSubAffiliate(null);
    setShowManualForm(false);
  };

  const editSubAffiliate = (row) => {
    setEditingSubAffiliate(row);
    setShowManualForm(true);
  };

  const deleteSubAffiliate = async (row) => {
    if (!window.confirm(`آیا از حذف "${row.name || row.mobile}" اطمینان دارید؟`)) {
      return;
    }
    try {
      await deleteApi({
        path: `admin/users/${affiliate.id}/sub-affiliates/${row._id}`,
      });
      successMessage("زیرنماینده با موفقیت حذف شد");
      await getSubAffiliates();
    } catch (error) {
      errorMessage(
        error?.response?.data?.errors?.[0]?.message || "خطا در حذف زیرنماینده"
      );
    }
  };

  const toggleManualForm = () => {
    setEditingSubAffiliate(null);
    setShowManualForm((v) => !v);
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const ws = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        const headerRow = rows[0] || [];

        const mobileIdx = findHeaderIndex(headerRow, ["موبایل", "شماره تماس", "mobile"]);
        const nameIdx = findHeaderIndex(headerRow, ["نام", "نام و نام خانوادگی", "name"]);
        const addressIdx = findHeaderIndex(headerRow, ["آدرس", "address"]);
        const cityIdx = findHeaderIndex(headerRow, ["شهر", "city"]);
        const commissionIdx = findHeaderIndex(headerRow, [
          "درصد کمیسیون",
          "کمیسیون",
          "درصد تخفیف",
          "commissionPercent",
        ]);
        const passwordIdx = findHeaderIndex(headerRow, ["رمز عبور", "پسورد", "password"]);
        const sabooraIdx = findHeaderIndex(headerRow, [
          "اجازه خرید صبورا دارد",
          "اجازه خرید صبورا",
          "sabooraAllowed",
        ]);
        const raychatIdx = findHeaderIndex(headerRow, [
          "دسترسی به چت بات",
          "دسترسی به چت‌بات",
          "raychatAllowed",
        ]);

        if (mobileIdx === -1 || mobileIdx === undefined) {
          errorMessage("ستون «موبایل» در فایل یافت نشد");
          setUploading(false);
          return;
        }

        const toBool = (v) =>
          ["true", "بله", "1", "yes"].includes(String(v || "").trim().toLowerCase());

        const parsedRows = rows
          .slice(1)
          .filter((r) => String(r[mobileIdx] || "").trim())
          .map((r) => ({
            mobile: String(r[mobileIdx] || "").trim(),
            name: nameIdx > -1 ? String(r[nameIdx] || "").trim() : undefined,
            address: addressIdx > -1 ? String(r[addressIdx] || "").trim() : undefined,
            city: cityIdx > -1 ? String(r[cityIdx] || "").trim() : undefined,
            commissionPercent:
              commissionIdx > -1 && r[commissionIdx] !== undefined
                ? Number(r[commissionIdx])
                : undefined,
            password:
              passwordIdx > -1 ? String(r[passwordIdx] || "").trim() || undefined : undefined,
            sabooraAllowed: sabooraIdx > -1 ? toBool(r[sabooraIdx]) : undefined,
            raychatAllowed: raychatIdx > -1 ? toBool(r[raychatIdx]) : undefined,
          }));

        if (parsedRows.length === 0) {
          errorMessage("هیچ ردیفی در فایل یافت نشد");
          setUploading(false);
          return;
        }

        await submitSubAffiliates(parsedRows);
      } catch (err) {
        console.error(err);
        errorMessage("خطا در پردازش فایل اکسل");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const saveParentPercent = async () => {
    const value = Number(parentPercentValue);
    if (Number.isNaN(value) || value < 0 || value > 100) {
      errorMessage("درصد باید عددی بین 0 تا 100 باشد");
      return;
    }
    setSavingParentPercent(true);
    try {
      await putApi({
        path: `admin/users/${affiliate.id}`,
        body: {
          extreaRefDiscount: value,
          linkCodes: affiliate.linkCodes || [],
        },
      });
      setAffiliate({ ...affiliate, extreaRefDiscount: value });
      successMessage("درصد کل نماینده با موفقیت به‌روزرسانی شد");
      setEditingParentPercent(false);
    } catch (error) {
      errorMessage(
        error?.response?.data?.errors?.[0]?.message || "خطا در به‌روزرسانی درصد نماینده"
      );
    } finally {
      setSavingParentPercent(false);
    }
  };

  if (!affiliate) return null;

  const subAffiliateRows = subAffiliates.map((item) => ({
    ...item,
    id: item._id,
    key: item._id,
  }));

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
        <Title title={"جزئیات نماینده"} />
        <div className="grid gap-4 mt-4 md:grid-cols-4">
          <div>
            <p className="text-xs text-gray-500">نام</p>
            <p className="text-sm font-medium">{affiliate.name || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">موبایل</p>
            <p className="text-sm font-medium">{affiliate.mobile}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">کد ارجاع</p>
            <p className="text-sm font-medium">{affiliate.refCode || "-"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">درصد کل نماینده</p>
            {editingParentPercent ? (
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={parentPercentValue}
                  onChange={(e) => setParentPercentValue(e.target.value)}
                  className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm dark:bg-gray-800 dark:border-gray-600"
                  autoFocus
                />
                <button
                  type="button"
                  disabled={savingParentPercent}
                  onClick={saveParentPercent}
                  className="text-xs font-medium text-green-600 hover:underline disabled:opacity-60"
                >
                  {savingParentPercent ? "در حال ذخیره..." : "ذخیره"}
                </button>
                <button
                  type="button"
                  disabled={savingParentPercent}
                  onClick={() => {
                    setParentPercentValue(affiliate.extreaRefDiscount ?? "");
                    setEditingParentPercent(false);
                  }}
                  className="text-xs font-medium text-gray-500 hover:underline disabled:opacity-60"
                >
                  انصراف
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm font-medium">
                  {affiliate.extreaRefDiscount || 0}%
                </p>
                <button
                  type="button"
                  onClick={() => setEditingParentPercent(true)}
                  className="text-xs font-medium text-blue-600 hover:underline"
                >
                  ویرایش
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <Title title={"زیرنماینده‌ها"} />
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              type="button"
              text={showManualForm ? "بستن فرم" : "افزودن دستی"}
              color="primary"
              className="whitespace-nowrap flex-1 sm:flex-none text-xs sm:text-sm"
              onClick={toggleManualForm}
            />
            <Button
              type="button"
              text={uploading ? "در حال آپلود..." : "آپلود فایل اکسل"}
              color="success"
              disabled={uploading}
              className="whitespace-nowrap flex-1 sm:flex-none text-xs sm:text-sm"
              onClick={() => fileInputRef.current?.click()}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelUpload}
              disabled={uploading}
              className="hidden"
            />
          </div>
        </div>

        {showManualForm && (
          <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <p className="text-sm font-semibold text-gray-700 mb-4">
              {editingSubAffiliate ? "ویرایش زیرنماینده" : "افزودن زیرنماینده دستی"}
            </p>
            <Formik
              enableReinitialize
              initialValues={{
                mobile: editingSubAffiliate?.mobile || "",
                password: "",
                name: editingSubAffiliate?.name || "",
                address: editingSubAffiliate?.address || "",
                city: editingSubAffiliate?.city || "",
                commissionPercent: editingSubAffiliate?.extreaRefDiscount || "",
                sabooraAllowed: editingSubAffiliate
                  ? String(!!editingSubAffiliate?.sabooraAllowed)
                  : "true",
                raychatAllowed: editingSubAffiliate
                  ? String(!!editingSubAffiliate?.raychatAllowed)
                  : "false",
              }}
              validationSchema={manualAddValidation(!!editingSubAffiliate)}
              onSubmit={manualFormHandler}
            >
              <Form>
                <div className="grid gap-4 md:grid-cols-3">
                  <Input
                    name="mobile"
                    label="موبایل"
                    type="text"
                    required
                    disabled={!!editingSubAffiliate}
                  />
                  {!editingSubAffiliate && (
                    <Input
                      name="password"
                      label="رمز عبور"
                      type="password"
                      required
                    />
                  )}
                  <Input name="name" label="نام" type="text" />
                  <Input name="city" label="شهر" type="text" />
                  <Input name="address" label="آدرس" type="text" />
                  <Input
                    name="commissionPercent"
                    label="درصد کمیسیون"
                    type="number"
                  />
                  <Field
                    as="select"
                    name="sabooraAllowed"
                    label="اجازه خرید صبورا دارد ؟"
                    component={Select}
                  >
                    <option value={"true"}>بله</option>
                    <option value={"false"}>خیر</option>
                  </Field>
                  <Field
                    as="select"
                    name="raychatAllowed"
                    label="دسترسی به چت‌بات"
                    component={Select}
                  >
                    <option value={"true"}>بله</option>
                    <option value={"false"}>خیر</option>
                  </Field>
                </div>
                <div className="mt-4">
                  <Button
                    text={editingSubAffiliate ? "ذخیره تغییرات" : "ثبت زیرنماینده"}
                    color="success"
                  />
                </div>
              </Form>
            </Formik>
          </div>
        )}

        <Table
          columns={subAffiliateColumns}
          rows={subAffiliateRows}
          notFoundMessage="هنوز زیرنماینده‌ای ثبت نشده است"
          onEdit={editSubAffiliate}
          onDelete={deleteSubAffiliate}
        />
      </div>
    </div>
  );
}
